import {
  Box,
  Grid,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ConsumptionsBillsSearchBar from "../../ConsumptionsBillsSearchBar/ConsumptionsBillsSearchBar";
import { OwnerDataCups } from "../../../core/models/ownerData/ownerData.model";
import {
  ConsumptionListData,
  ConsumptionFormatted,
  ConsumptionsPeriodInformation,
  GetGraphDataRequestBody,
  SelectedDataPeriodConsumptions,
} from "../../../core/models/consumptions/consumptions.model";
import { useEffect, useRef, useState } from "react";
import EmptyStateComponent from "../../UI/EmptyStateComponent/EmptyStateComponent";
import theme from "../../../muiTheme";
import { UserData } from "../../../core/models/login/login.model";
import { getGraphDataPeriodConsumptions } from "../../../core/services/consumptions/consumptions.service";
import LoadingComponentComponent from "../../UI/LoadingComponentComponent/LoadingComponentComponent";
import { formatStringDate } from "../../../utils/dateUtils/formatDate.util";
import { Chart, ChartEvent } from "chart.js";
import { formatNumber } from "../../../utils/formatters/formatters.util";
import {
  formatConsumptions,
  getBarPositionX,
  getBarChart,
  handleClickBarChart,
  getStackedDatasets,
} from "../../../utils/components/consumptions/consumptions.util";
import { ActiveElement } from "chart.js/dist/plugins/plugin.tooltip";
import "./ConsumptionsPeriod.scss";
import { useTranslation } from "react-i18next";
import { SearchParams } from "../../../core/models/consumptionsBillsSearchBar/consumptionsBillsSearchBar.model";
import { CustomDateError } from "../../../core/models/datePickersComponent/datePickersComponent.model";
import { isEmpty } from "lodash";

interface ConsumptionsPeriodProps {
  ownerDataCups: OwnerDataCups[];
  userData: UserData | null;
  consumptionsList: ConsumptionFormatted[];
  setConsumptionsList: React.Dispatch<
    React.SetStateAction<ConsumptionFormatted[]>
  >;
  consumptionsPeriodInformation: ConsumptionsPeriodInformation;
  setConsumptionsPeriodInformation: React.Dispatch<
    React.SetStateAction<ConsumptionsPeriodInformation>
  >;
  searchParams: SearchParams;
  setSearchParams: React.Dispatch<React.SetStateAction<SearchParams>>;
  selectedDataPeriod: SelectedDataPeriodConsumptions;
  setSelectedDataPeriod: React.Dispatch<
    React.SetStateAction<SelectedDataPeriodConsumptions>
  >;
}

const ConsumptionsPeriod = ({
  ownerDataCups,
  userData,
  consumptionsList,
  setConsumptionsList,
  consumptionsPeriodInformation,
  setConsumptionsPeriodInformation,
  searchParams,
  setSearchParams,
  selectedDataPeriod: { elements, consumption, positionX },
  setSelectedDataPeriod,
}: ConsumptionsPeriodProps) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSearchActive, setIsSearchActive] = useState<boolean>(false);
  const [isFirstChartLoadFlag, setIsFirstChartLoadFlag] =
    useState<boolean>(true);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDesktopViewport = useMediaQuery("(min-width: 1100px )");
  const chart = useRef<Chart | null>(null);
  const [environment, setEnvironment] = useState<"Gas" | "Luz">("Luz");

  const [totalOptionChecked, setTotalOptionChecked] = useState<boolean>(false);

  useEffect(() => {
    if (canvasRef.current) {
      chart.current = getBarChart(
        environment,
        canvasRef.current,
        consumptionsList,
        isDesktopViewport,
        (elements: ActiveElement[], event: ChartEvent) => {
          handleClickBarChart(elements, chart.current as Chart);
          setSelectedDataPeriod({
            consumption: consumptionsList[elements[0].index],
            positionX: getBarPositionX(chart.current as Chart, event),
            elements,
          });
        },
        getStackedDatasets(consumptionsList),
        true
      );

      return () => {
        (chart.current as Chart).destroy();
      };
    }
  }, [consumptionsList, isDesktopViewport, setSelectedDataPeriod]);

  useEffect(() => {
    if (isFirstChartLoadFlag && chart.current && consumptionsList.length) {
      // handleClickBarChart(elements, chart.current as Chart);
      setIsFirstChartLoadFlag(false);
      return;
    }

    if (!isFirstChartLoadFlag && !consumptionsList.length) {
      setIsFirstChartLoadFlag(true);
    }
  }, [isFirstChartLoadFlag, consumptionsList, chart]);

  const consumptionListData: ConsumptionListData[] = [
    {
      id: "selectedPeriod",
      label: "Periodo seleccionado",
      value:
        isEmpty(consumptionsPeriodInformation.dateFrom) ||
        isEmpty(consumptionsPeriodInformation.dateTo)
          ? t("Total period")
          : `${consumptionsPeriodInformation.dateFrom} - ${consumptionsPeriodInformation.dateTo}`,
    },
    {
      id: "totalActiveEnergy",
      label: "Total de energía activa",
      value: `${consumptionsPeriodInformation.totalActiveEnergy} ${
        environment === "Gas" ? "m3" : "kWh"
      }`,
    },
    {
      id: "totalConsumedEnergy",
      label: "Total de energía consumida",
      value: `${consumptionsPeriodInformation.totalConsumedEnergy} ${
        environment === "Gas" ? "m3" : "kWh"
      }`,
    },
  ];

  const consumptionListDates: ConsumptionListData[] = [
    {
      id: "readingDate",
      label: "Fecha de lectura",
      value: `${consumption?.invoiceDate}`,
    },
    {
      id: "period",
      label: "Periodo del",
      value: `${consumption?.dateFrom} - ${consumption?.dateTo}`,
    },
  ];

  const handleClickSearch = (searchParams: GetGraphDataRequestBody) => {
    setIsLoading(true);
    getGraphDataPeriodConsumptions(searchParams, userData?.token || "")
      .then(
        ({ totalEnergiaActiva, totalEnergiaConsumida, consumptionList }) => {
          setConsumptionsList(formatConsumptions(consumptionList));

          const cupsId = searchParams.cupsId;
          const cupsEnvironment = ownerDataCups.find(
            (cups) => cups.idCups === cupsId
          )?.envirorment;
          setEnvironment(cupsEnvironment === "Gas" ? "Gas" : "Luz");
          setConsumptionsPeriodInformation({
            totalActiveEnergy: formatNumber(totalEnergiaActiva),
            totalConsumedEnergy: formatNumber(totalEnergiaConsumida),
            dateFrom: formatStringDate(searchParams.dateFrom),
            dateTo: formatStringDate(searchParams.dateTo),
          });
          setSelectedDataPeriod((prevSelectedDataPeriod) => ({
            ...prevSelectedDataPeriod,
            consumption: null,
          }));
          setIsLoading(false);
          setIsSearchActive(true);
        }
      )
      .catch((error) => {
        setIsLoading(false);
      });
  };

  const customDateError: CustomDateError[] = [
    {
      errorType: "minDate",
      text: t(
        "Datepicker.La fecha inicial no puede ser posterior a la fecha final"
      ),
    },
  ];

  return (
    <Stack
      component="article"
      rowGap={"2.5rem"}
      padding={{ xs: "1rem 0", md: "40px 0 80px 0" }}
    >
      <ConsumptionsBillsSearchBar
        ownerDataCups={ownerDataCups}
        handleClickSearch={handleClickSearch}
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        isLoading={isLoading}
        isChartLoad
        isFirstChartLoadFlag={isFirstChartLoadFlag}
        customDateError={customDateError}
        totalOptionIsChecked={totalOptionChecked}
        setTotalOptionIsChecked={setTotalOptionChecked}
      />
      {!isLoading ? (
        <>
          {!!consumptionsList.length ? (
            <Paper sx={{ width: "100%", borderRadius: "12px" }}>
              <Box
                display="flex"
                flexDirection="row"
                flexWrap="wrap"
                borderBottom={`solid 2px ${theme.palette.grey[100]}`}
                paddingX="2rem"
                paddingY="1.5rem"
                gap="4rem"
              >
                {consumptionListData.map(({ id, label, value }) => (
                  <Stack direction="column" gap="0.5rem" key={id}>
                    <Typography
                      component="p"
                      variant="body2"
                      sx={{ color: "grey.500" }}
                    >
                      {t(`Consumos.${label}`)}
                    </Typography>
                    <Typography component="h3" variant="subtitle1">
                      {value}
                    </Typography>
                  </Stack>
                ))}
              </Box>
              <Stack component="div">
                <Box>
                  <canvas
                    id="chart"
                    ref={canvasRef}
                    style={{ height: "600px" }}
                  />
                </Box>

                <Stack
                  component="div"
                  height={"auto"}
                  bgcolor={theme.palette.grey[50]}
                  border={`1px solid ${theme.palette.grey[300]}`}
                  borderRadius={"12px"}
                  margin={{ xs: "1rem", md: "3rem" }}
                  paddingY={"3rem"}
                  paddingX={{ xs: "1.5rem", md: "6rem" }}
                  position={"relative"}
                  gap={"1rem"}
                >
                  {consumption ? (
                    <>
                      <Box
                        component={"div"}
                        position={"absolute"}
                        bgcolor={theme.palette.grey[50]}
                        sx={{
                          borderTop: `1px solid ${theme.palette.grey[300]}`,
                          borderLeft: `1px solid ${theme.palette.grey[300]}`,
                        }}
                        width={"20px"}
                        height={"20px"}
                        top={-11}
                        left={positionX ? positionX - 60 : 0}
                        className="arrow-graph"
                      />
                      {consumptionListDates.map(({ id, label, value }) => (
                        <Typography
                          key={id}
                          component={"p"}
                          variant={"subtitle2"}
                        >
                          {t(`Consumos.${label}`)}
                          <Typography component={"span"} fontWeight={600}>
                            {` ${value}`}
                          </Typography>
                        </Typography>
                      ))}
                      <Grid container columnSpacing={{ xs: 12, sm: 8, md: 6 }}>
                        <Grid item paddingTop="1rem">
                          <Typography
                            component={"p"}
                            variant="body1"
                            color={theme.palette.grey[500]}
                          >
                            {t(`Consumos.Energía Activa`)}
                          </Typography>
                          <Typography component={"p"} variant="subtitle1">{`${
                            consumption.totalActiveConsumption
                          } ${
                            environment === "Gas" ? "m3" : "kWh"
                          }`}</Typography>
                        </Grid>
                        {consumption.periodPowers.map((period, indexNumber) => (
                          <Grid item key={period} paddingTop="1rem">
                            <Typography
                              component={"p"}
                              variant="body1"
                              color={theme.palette.grey[500]}
                            >
                              {period}
                            </Typography>
                            <Typography
                              component={"p"}
                              variant="subtitle1"
                            >{`${formatNumber(
                              consumption.activePowers[indexNumber]
                            )} ${
                              environment === "Gas" ? "m3" : "kWh"
                            }`}</Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  ) : (
                    <Stack
                      display={"flex"}
                      flex-direction={"column"}
                      alignContent={"center"}
                      alignItems={"center"}
                      rowGap={2}
                    >
                      <Typography
                        component={"span"}
                        variant="h3"
                        className="t-icon-select round-icon"
                      ></Typography>
                      <Typography
                        component={"p"}
                        variant={"subtitle2"}
                        color={theme.palette.grey[700]}
                        margin={"auto"}
                      >
                        {t(
                          `Consumos.Selecciona una columna para consultar el detalle de consumo`
                        )}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </Paper>
          ) : (
            <>
              {isSearchActive ? (
                <EmptyStateComponent
                  text={
                    "Consumos.Selecciona otro periodo para mostrar consumos"
                  }
                  height={"30rem"}
                />
              ) : (
                <EmptyStateComponent
                  text={
                    "Consumos.Selecciona un suministro y un periodo para mostrar consumos"
                  }
                  height={"30rem"}
                />
              )}
            </>
          )}
        </>
      ) : (
        <Paper
          sx={{
            width: "100%",
            borderRadius: "12px",
            height: "30rem",
            position: "relative",
          }}
        >
          <LoadingComponentComponent isLoading={isLoading} />
        </Paper>
      )}
    </Stack>
  );
};

export default ConsumptionsPeriod;
